/**
 * FW-2026 Product PDP enhancements
 * - Gallery swipe / fade helpers
 * - Accordion single-open
 * - Sticky mobile ATC
 * - Recently viewed (localStorage)
 */
(function () {
  const RECENT_KEY = 'fw2026_recently_viewed';
  const RECENT_LIMIT = 12;

  function qs(root, sel) {
    return (root || document).querySelector(sel);
  }

  function qsa(root, sel) {
    return Array.from((root || document).querySelectorAll(sel));
  }

  /* ---- Recently viewed ---- */
  function trackRecentlyViewed() {
    const el = qs(document, '[data-fw-recent-product]');
    if (!el) return;
    const handle = el.getAttribute('data-fw-recent-product');
    if (!handle) return;
    let list = [];
    try {
      list = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
    } catch (e) {
      list = [];
    }
    if (!Array.isArray(list)) list = [];
    list = list.filter((h) => h && h !== handle);
    list.unshift(handle);
    localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, RECENT_LIMIT)));
  }

  async function loadRecentlyViewedSection(sectionEl) {
    if (!sectionEl) return;
    let list = [];
    try {
      list = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
    } catch (e) {
      list = [];
    }
    const current = sectionEl.getAttribute('data-product-handle');
    list = list.filter((h) => h && h !== current).slice(0, Number(sectionEl.getAttribute('data-limit') || 4));
    if (!list.length) {
      sectionEl.classList.add('is-empty');
      sectionEl.hidden = true;
      return;
    }

    const grid = qs(sectionEl, '[data-fw-recent-grid]');
    if (!grid) return;

    const cards = await Promise.all(
      list.map(async (handle) => {
        try {
          const res = await fetch(`/products/${handle}.js`);
          if (!res.ok) return null;
          const product = await res.json();
          return buildRecentCard(product);
        } catch (e) {
          return null;
        }
      })
    );

    const html = cards.filter(Boolean).join('');
    if (!html) {
      sectionEl.classList.add('is-empty');
      sectionEl.hidden = true;
      return;
    }
    grid.innerHTML = html;
    sectionEl.hidden = false;
    sectionEl.classList.remove('is-empty');
  }

  function buildRecentCard(product) {
    if (!product) return '';
    const img =
      product.featured_image ||
      (product.images && product.images[0]) ||
      '';
    const price = (product.price / 100).toLocaleString(undefined, {
      style: 'currency',
      currency: (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD',
    });
    const url = `/products/${product.handle}`;
    return `
      <li class="fw-2026-recs__item">
        <a class="fw-2026-card" href="${url}">
          <div class="fw-2026-card__media">
            ${img ? `<img class="fw-2026-card__media-primary" src="${img}" alt="${escapeHtml(product.title)}" loading="lazy" width="400" height="400">` : ''}
          </div>
          <h3 class="fw-2026-card__title">${escapeHtml(product.title)}</h3>
          <div class="fw-2026-card__price">${price}</div>
        </a>
      </li>`;
  }

  function escapeHtml(str) {
    return String(str || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  /* ---- Sticky ATC ---- */
  function initStickyAtc(section) {
    const sticky = qs(section, '[data-fw-sticky-atc]');
    const trigger = qs(section, '[data-fw-atc-anchor]');
    if (!sticky || !trigger) return;

    const io = new IntersectionObserver(
      ([entry]) => {
        if (window.matchMedia('(min-width: 990px)').matches) {
          sticky.classList.remove('is-visible');
          return;
        }
        sticky.classList.toggle('is-visible', !entry.isIntersecting && entry.boundingClientRect.top < 0);
        sticky.setAttribute('aria-hidden', sticky.classList.contains('is-visible') ? 'false' : 'true');
      },
      { threshold: 0 }
    );
    io.observe(trigger);

    const stickyBtn = qs(sticky, '[data-fw-sticky-submit]');
    const mainBtn = qs(section, '[data-fw-main-submit]');
    if (stickyBtn && mainBtn) {
      const syncStickyLoading = () => {
        const isLoading = mainBtn.classList.contains('loading');
        stickyBtn.classList.toggle('loading', isLoading);
        stickyBtn.disabled = mainBtn.disabled || mainBtn.getAttribute('aria-disabled') === 'true';
        const spinner = stickyBtn.querySelector('.loading__spinner');
        if (spinner) spinner.classList.toggle('hidden', !isLoading);
      };

      stickyBtn.addEventListener('click', (e) => {
        e.preventDefault();
        if (!mainBtn.disabled && mainBtn.getAttribute('aria-disabled') !== 'true') mainBtn.click();
      });

      new MutationObserver(syncStickyLoading).observe(mainBtn, {
        attributes: true,
        attributeFilter: ['class', 'disabled', 'aria-disabled'],
      });
    }
  }

  /* ---- Gallery: horizontal carousel (Swix reference) ---- */
  function initGallery(section) {
    const gallery = qs(section, 'media-gallery.fw-2026-gallery, .fw-2026-gallery');
    if (!gallery || gallery.dataset.fwGalleryInit === 'true') return;
    gallery.dataset.fwGalleryInit = 'true';

    const track = qs(gallery, '[data-fw-gallery-track], .fw-2026-gallery__list');
    const viewport = qs(gallery, '[data-fw-gallery-viewport], .fw-2026-gallery__viewport') || track?.parentElement;
    if (!track) return;

    const items = () => qsa(track, '.fw-2026-gallery__item[data-media-id]');
    const prev = qs(gallery, '[data-fw-gallery-prev]');
    const next = qs(gallery, '[data-fw-gallery-next]');
    const status = qs(gallery, '[role="status"]');
    let index = Math.max(
      0,
      items().findIndex((i) => i.classList.contains('is-active'))
    );
    let dragStartX = 0;
    let dragDelta = 0;
    let dragging = false;

    function updateNavState(i) {
      const all = items();
      const idx = typeof i === 'number' ? i : index;
      if (prev) prev.disabled = idx <= 0;
      if (next) next.disabled = idx >= all.length - 1 || all.length < 2;
      if (status && all.length) {
        status.textContent = `Image ${idx + 1} of ${all.length}`;
      }
    }

    function goTo(nextIndex, { animate = true } = {}) {
      const all = items();
      if (!all.length) return;
      const clamped = Math.max(0, Math.min(nextIndex, all.length - 1));
      index = clamped;

      if (!animate) track.style.transition = 'none';
      track.style.transform = `translate3d(-${index * 100}%, 0, 0)`;
      if (!animate) {
        // Force reflow then restore transition
        void track.offsetWidth;
        track.style.transition = '';
      }

      all.forEach((item, i) => item.classList.toggle('is-active', i === index));
      const id = all[index].getAttribute('data-media-id');
      qsa(gallery, '[data-fw-thumb]').forEach((t) => {
        t.setAttribute('aria-current', t.getAttribute('data-target') === id ? 'true' : 'false');
      });
      updateNavState(index);
    }

    qsa(gallery, '[data-fw-thumb]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const id = btn.getAttribute('data-target');
        const idx = items().findIndex((item) => item.getAttribute('data-media-id') === id);
        if (idx >= 0) goTo(idx);
      });
    });

    if (prev) prev.addEventListener('click', () => goTo(index - 1));
    if (next) next.addEventListener('click', () => goTo(index + 1));

    // Touch / pen swipe only — mouse must keep native text selection
    const onPointerDown = (e) => {
      if (e.pointerType === 'mouse') return;
      if (e.button !== 0 && e.button !== undefined) return;
      dragging = true;
      dragStartX = e.clientX;
      dragDelta = 0;
      track.style.transition = 'none';
      if (viewport.setPointerCapture) {
        try {
          viewport.setPointerCapture(e.pointerId);
        } catch (_) {}
      }
    };

    const onPointerMove = (e) => {
      if (!dragging) return;
      dragDelta = e.clientX - dragStartX;
      const width = viewport.offsetWidth || 1;
      const percent = (dragDelta / width) * 100;
      track.style.transform = `translate3d(calc(-${index * 100}% + ${percent}%), 0, 0)`;
    };

    const onPointerUp = () => {
      if (!dragging) return;
      dragging = false;
      track.style.transition = '';
      const width = viewport.offsetWidth || 1;
      const threshold = width * 0.18;
      if (dragDelta > threshold) goTo(index - 1);
      else if (dragDelta < -threshold) goTo(index + 1);
      else goTo(index);
      dragDelta = 0;
    };

    if (viewport) {
      viewport.addEventListener('pointerdown', onPointerDown);
      viewport.addEventListener('pointermove', onPointerMove);
      viewport.addEventListener('pointerup', onPointerUp);
      viewport.addEventListener('pointercancel', onPointerUp);
    }

    // Sync when variant media changes active class externally
    const mo = new MutationObserver(() => {
      const activeIdx = items().findIndex((i) => i.classList.contains('is-active'));
      if (activeIdx >= 0 && activeIdx !== index) goTo(activeIdx, { animate: false });
      else updateNavState();
    });
    mo.observe(track, { attributes: true, subtree: true, attributeFilter: ['class'] });

    goTo(index, { animate: false });

    if (gallery.classList.contains('fw-2026-gallery--zoom')) {
      qsa(gallery, '.fw-2026-gallery__zoom-wrap').forEach((wrap) => {
        wrap.addEventListener('mousemove', (e) => {
          const rect = wrap.getBoundingClientRect();
          const x = ((e.clientX - rect.left) / rect.width) * 100;
          const y = ((e.clientY - rect.top) / rect.height) * 100;
          const img = qs(wrap, 'img');
          if (img) img.style.transformOrigin = `${x}% ${y}%`;
        });
      });
    }
  }

  /* ---- Keep accordion column width matched to product info ---- */
  const INFO_WIDTH_MQ = '(min-width: 990px)';
  let infoWidthObserver = null;
  let infoWidthSyncBound = false;

  function syncInfoColumnWidth() {
    const info = qs(document, '.fw-2026__info');
    const accordionMains = qsa(document, '.fw-2026-accordion__main');
    const root = document.documentElement;
    const isDesktop = window.matchMedia(INFO_WIDTH_MQ).matches;

    if (!info || !accordionMains.length || !isDesktop) {
      root.style.removeProperty('--fw-synced-info-width');
      accordionMains.forEach((el) => el.style.removeProperty('--fw-synced-info-width'));
      return;
    }

    const widthPx = `${Math.round(info.getBoundingClientRect().width)}px`;
    root.style.setProperty('--fw-synced-info-width', widthPx);
    accordionMains.forEach((el) => el.style.setProperty('--fw-synced-info-width', widthPx));
  }

  function initInfoWidthSync() {
    const info = qs(document, '.fw-2026__info');
    syncInfoColumnWidth();

    if (infoWidthObserver) {
      infoWidthObserver.disconnect();
      infoWidthObserver = null;
    }

    if (info && typeof ResizeObserver !== 'undefined') {
      infoWidthObserver = new ResizeObserver(() => {
        requestAnimationFrame(syncInfoColumnWidth);
      });
      infoWidthObserver.observe(info);
    }

    if (!infoWidthSyncBound) {
      infoWidthSyncBound = true;
      window.addEventListener('resize', () => {
        requestAnimationFrame(syncInfoColumnWidth);
      });
    }
  }

  function initSection(section) {
    if (!section || section.dataset.fwInit === 'true') return;
    section.dataset.fwInit = 'true';
    initGallery(section);
    initStickyAtc(section);
  }

  function boot() {
    trackRecentlyViewed();
    qsa(document, '[data-fw-2026-product]').forEach(initSection);
    initInfoWidthSync();
    qsa(document, '[data-fw-recently-viewed]').forEach(loadRecentlyViewedSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', (e) => {
    const section = e.target.querySelector('[data-fw-2026-product]') || e.target;
    if (section.matches && section.matches('[data-fw-2026-product]')) initSection(section);
    initInfoWidthSync();
    qsa(e.target, '[data-fw-recently-viewed]').forEach(loadRecentlyViewedSection);
  });
})();
